import React, { useEffect, useState } from "react";
import { useAuth } from "../auth/FirebaseAuth.js";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getUserById } from "../models/UserFunctions.js"; // Function to fetch user data
import { getOrgById, getOrgByRef } from "../models/OrgFunctions.js"; // Functions to fetch org and client data
import {
  getArrayDocDataByID,
  getArrayDocDataByRef,
  getDocDataByRef,
  listenArrayDocDataByRef,
  listenDocDataByRef,
  queryCollection,
} from "../models/DataFunctions.js";
import Divider from "../layout/Divider.js";
import Space from "../layout/Space.js";
import ConvMessage from "./ConvMessage.js"; // Assuming this component exists and works
import { Skeleton, useTheme } from "@mui/material";
import SkeletonConvMessage from "./SkeletonConvMessage.js";
import { getOrRefreshFirebaseToken } from "../config/getFirebaseToken.js";
import getTemporaryUrl from "../api/getTemporaryUrl.js";
import { useAppContext } from "../config/AppContext.js";
import loadingAnimation from "../animations/loading.json"; // Path to your JSON file
import { Player } from "@lottiefiles/react-lottie-player";
import Icon1 from "../icons/Icon.js";
import FolderChat from "./FolderChat.js";
import { Spin } from "antd";
import SmallSpinner from "../loading/SmallSpinner.js";
import { queryFolders } from "../models/AlgoliaFunctions.js";
import SearchAlgolia from "../textfields/SearchAlgolia.js";
import {
  ChatApp,
  NotificationManager,
} from "../config/FirebaseNotificationSystem.js";
import PrimaryButton from "../buttons/PrimaryButton.js";

function Messages({ navigateWorkspace, orgData, userData, workspaceData }) {
  const theme = useTheme();
  const { currentUser, loading } = useAuth();
  const { appState, setAppState, setFilter, filter } = useAppContext();
  // searchParams folder_id
  const [searchParams] = useSearchParams();
  const redirect_folder_id = searchParams.get("folder_id", null);
  const prefix = appState.dev ? appState.dev_url : appState.prod_url;
  const saved_data = appState.saved_data;

  const [message_folders, setMessageFolders] = useState([]);
  const itemsPerPage = 15;
  const [loadingData, setLoadingData] = useState(false);

  const [selectedFolder, setSelectedFolder] = useState(null);
  const [selectedFolderMessages, setSelectedFolderMessages] = useState([]);
  const [unsubscribe, setUnsubscribe] = useState(null);

  const [inputValue, setInputValue] = useState("");
  const [receiver, setReceiver] = useState(null);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [users, setUsers] = useState([]);
  // Function to fetch data for the current user
  const handleFetchData = async () => {
    if (userData && orgData) {
      setLoadingData(true);

      // Fetch user data by currentUser's ID

      let all_folders = await queryFolders(
        workspaceData,
        orgData,
        !workspaceData
          ? [
              {
                field: "notified_orgs_id",
                operator: "array-contains",
                value: orgData.id,
              },
            ]
          : []
      );
      let users =
        (await getArrayDocDataByID("user", orgData.users_id || [])) || [];
      setUsers(users);
      const message_folders = all_folders.filter((folder) => {
        return (
          folder.notified_orgs_id &&
          folder.notified_orgs_id.find((org_id) => org_id === orgData.id)
        );
      });

      const sorted_message_folders = message_folders.sort((a, b) => {
        if (a.last_time_posted && b.last_time_posted) {
          return b.last_time_posted.seconds - a.last_time_posted.seconds;
        }
        return 0;
      });

      setMessageFolders(sorted_message_folders);
      if (
        sorted_message_folders &&
        sorted_message_folders.length > 0 &&
        redirect_folder_id
      ) {
        let matching_folder = sorted_message_folders.find(
          (folder) => folder.id === redirect_folder_id
        );
        if (matching_folder) {
          handleSwitchFolder(matching_folder);
        }
      }
      setLoadingData(false);
    } else {
      console.error("No currentUser found");
    }
  };

  // Fetch data when the component mounts or when currentUser changes
  useEffect(() => {
    handleFetchData();
  }, [userData, orgData]);

  // Cleanup listener on unmount
  useEffect(() => {
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [unsubscribe]);

  const handleRefreshMessages = async (folderconv) => {
    const messages = await getArrayDocDataByRef(folderconv?.messages || []);
    setSelectedFolderMessages(messages);
  };

  const handleSwitchFolder = async (folder) => {
    setLoadingMessages(true);
    let fresh_folder = await getDocDataByRef(folder.ref);

    setSelectedFolder(fresh_folder);
    const token = await getOrRefreshFirebaseToken();
    await getTemporaryUrl(token, "messages", "none", folder.id, prefix);
    fresh_folder = await getDocDataByRef(folder.ref);
    setSelectedFolder(fresh_folder);
    if (unsubscribe) {
      unsubscribe();
    }

    if (fresh_folder.client_type !== "particulier") {
      const client = await getOrgById(folder.client_id);
      setReceiver({
        value: client.id,
        label: client.nom,
      });
    } else {
      setReceiver({
        value: "particulier",
        label: "Particulier",
      });
    }
    const folderconv = await getDocDataByRef(folder.folderconv);
    // Listen for real-time updates
    const newUnsubscribe = listenDocDataByRef(
      folderconv.ref,
      handleRefreshMessages
    );
    setUnsubscribe(() => newUnsubscribe);
    setLoadingMessages(false);
  };

  if (loading || !currentUser || !userData || !orgData) {
    return (
      <div className="flex flex-col h-full w-full">
        <SmallSpinner></SmallSpinner>
      </div>
    );
  }

  const getItemSize = (index) => 80; // Height of each item, can be dynamic if needed

  return (
    <div className="flex flex-col h-full w-full max-h-full">
      <div className="flex flex-row w-full  h-full">
        <div className="flex flex-col h-full w-full ">
          <div className="flex flex-row gap-6 items-center ">
            <div className="text-hx font-medium pl-4 ">Messages non lus</div>
            <SearchAlgolia
              options={message_folders

                .filter((folder) =>
                  folder.nom.toLowerCase().includes(inputValue.toLowerCase())
                )
                .map((folder) => ({
                  label: folder.nom,
                  value: folder.id,
                }))}
              setInputValue={(v) => {
                setInputValue(v);
              }}
              onAction={(value) => {
                const folder = message_folders.find(
                  (f) => f.id === value.value
                );
                if (folder) {
                  handleSwitchFolder(folder);
                }
              }}
            ></SearchAlgolia>
          </div>
          <Space y={20}></Space>
          {loadingData && (
            <div className="p-4">
              <SmallSpinner></SmallSpinner>
            </div>
          )}
          <div className="w-full h-full pb-4 gap-1 pr-4 flex flex-row max-h-full pl-4">
            {/* Virtualized List for Messages */}
            <div className=" w-2/5 flex flex-col h-full max-h-full">
              {message_folders.length === 0 && (
                <div className="flex flex-col w-full h-full  items-center justify-center  rounded-md p-4">
                  <div className="text-h2 font-medium w-c300 text-center text-shadow_50 flex flex-col gap-4 items-center ">
                    <Icon1 icon_name="done2" size={30} fill="grey" />
                    Tous les messages ont été lus
                  </div>
                </div>
              )}
              {message_folders.length > 0 && (
                <div className="h-5/6 max-h-full">
                  <div className="flex flex-col h-full max-h-[80vh]  overflow-y-auto pr-4 ">
                    {message_folders.map((folder, index) => (
                      <div
                        key={message_folders[index].id}
                        onClick={() =>
                          handleSwitchFolder(message_folders[index])
                        }
                      >
                        <ConvMessage
                          message_folder={message_folders[index]}
                          isSelected={
                            selectedFolder?.id === message_folders[index].id
                          }
                          orgData={orgData}
                          users={users}
                          workspaceData={workspaceData}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="max-h-full w-3/5 flex ">
              {loadingMessages && (
                <div
                  className="
                 animate-scale-in 
                p-10 w-full h-full flex flex-col gap-2"
                >
                  <div className=" w-full  h-full justify-center items-center flex bg-shadow_10 animate-shimmer rounded-xl">
                    <SmallSpinner></SmallSpinner>
                  </div>
                </div>
              )}

              {!loadingMessages && selectedFolder && selectedFolderMessages && (
                <div className="max-h-[80vh] w-full">
                  <FolderChat
                    folder={selectedFolder}
                    messages={selectedFolderMessages}
                    orgData={orgData}
                    onUpdate={handleFetchData}
                    receiver={receiver}
                    can_go_folder={true}
                    setReceiver={setReceiver}
                    navigateWorkspace={navigateWorkspace}
                    workspaceData={workspaceData}
                    userData={userData}
                  ></FolderChat>
                </div>
              )}

              {!loadingMessages && !selectedFolder && (
                <div className="flex flex-col w-full h-full  items-center justify-center bg-neutral-100 rounded-xl p-4">
                  <div className="text-h2 font-medium text-shadow_25 flex flex-col gap-4 items-center ">
                    <Icon1 icon_name="message" size={30} fill="lightgrey" />
                    Aucune conversation sélectionnée
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Messages;
